import type { EnvironmentId } from "@afar-research/contracts";

export interface DiscoveryEnvironmentRows<TProject, TThread> {
  readonly projects: ReadonlyArray<TProject>;
  readonly threads: ReadonlyArray<TThread>;
}

/**
 * Choose the rows thread discovery projects over.
 *
 * The shell state retains the last snapshot an Environment delivered, so rows stay in
 * memory while it reconnects. Those retained rows are authoritative and are always
 * preferred; the device-local cache only fills in for a disconnected Environment that
 * has delivered nothing this session, such as a cold start before the first sync.
 */
export function selectDiscoveryRows<
  TProject extends { readonly environmentId: EnvironmentId },
  TThread extends { readonly environmentId: EnvironmentId },
>(input: {
  readonly projects: ReadonlyArray<TProject>;
  readonly threads: ReadonlyArray<TThread>;
  readonly cachedRowsByEnvironmentId: ReadonlyMap<
    EnvironmentId,
    DiscoveryEnvironmentRows<TProject, TThread>
  >;
  readonly readinessByEnvironmentId: ReadonlyMap<EnvironmentId, boolean>;
}): DiscoveryEnvironmentRows<TProject, TThread> {
  const retainedEnvironmentIds = new Set<EnvironmentId>();
  for (const project of input.projects) retainedEnvironmentIds.add(project.environmentId);
  for (const thread of input.threads) retainedEnvironmentIds.add(thread.environmentId);

  const cachedRows = [...input.cachedRowsByEnvironmentId.entries()].filter(
    ([environmentId]) =>
      input.readinessByEnvironmentId.get(environmentId) === false &&
      !retainedEnvironmentIds.has(environmentId),
  );

  return {
    projects: [...input.projects, ...cachedRows.flatMap(([, rows]) => rows.projects)],
    threads: [...input.threads, ...cachedRows.flatMap(([, rows]) => rows.threads)],
  };
}
